// B2B Settings Page - URL-based Tab Navigation
import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useB2B } from '../context/B2BContext';
import AISettingsSection from '../components/AISettingsSection';

export default function B2BSettings() {
  const { agency, refreshAgency, token } = useB2B();
  const [searchParams] = useSearchParams();
  
  // Get tab from URL, default to 'branding'
  const activeTab = searchParams.get('tab') || 'branding';
  
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const fileInputRef = useRef(null);
  
  const [settings, setSettings] = useState({
    name: '',
    brandColor: '#d4a853',
    logoEmoji: '✈️',
    logoUrl: '',
    greeting: '',
    currency: 'USD'
  });

  // Notification settings (separate state)
  const [notificationSettings, setNotificationSettings] = useState({
    frequency: 'instant',
    email: ''
  });
  const [notificationSaving, setNotificationSaving] = useState(false);
  const [notificationSaveSuccess, setNotificationSaveSuccess] = useState(false);

  // Widget behavior settings (separate state)
  const [widgetSettings, setWidgetSettings] = useState({
    position: 'bottom-right',
    autoOpenDelay: 0  // 0 = never, or seconds
  });
  const [widgetSaving, setWidgetSaving] = useState(false);
  const [widgetSaveSuccess, setWidgetSaveSuccess] = useState(false);

  // Tab titles for page header
  const tabTitles = {
    branding: { title: 'Branding', subtitle: 'Customize how your widget looks on your website' },
    widget: { title: 'Widget Settings', subtitle: 'Configure widget behavior and appearance' },
    ai: { title: 'AI Settings', subtitle: 'Configure how the AI assistant behaves' },
    notifications: { title: 'Notifications', subtitle: 'Configure how you receive lead notifications' },
    embed: { title: 'Embed Code', subtitle: 'Add the chat widget to your website' }
  };

  const currentTab = tabTitles[activeTab] || tabTitles.branding;

  // Only initialize once when agency first loads
  useEffect(() => {
    if (agency && !initialized) {
      setSettings({
        name: agency.name || '',
        brandColor: agency.branding?.brand_color || agency.brand_color || '#d4a853',
        logoEmoji: agency.branding?.logo_emoji || agency.logo_emoji || '✈️',
        logoUrl: agency.branding?.logo_url || agency.logo_url || '',
        greeting: agency.branding?.widget_greeting || agency.widget_greeting || `Hi! I'm your AI travel assistant. Where would you like to go?`,
        currency: agency.currency || 'USD'
      });
      
      // Load notification settings
      setNotificationSettings({
        frequency: agency.notification_frequency || 'instant',
        email: agency.notification_email || ''
      });
      
      // Load widget behavior settings
      setWidgetSettings({
        position: agency.widget_position || 'bottom-right',
        autoOpenDelay: agency.auto_open_delay || 0
      });
      
      setInitialized(true);
    }
  }, [agency, initialized]);

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setSaveSuccess(false);
  };

  // Handle logo file upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a valid image file (JPG, PNG, GIF, WebP, or SVG)');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('Logo must be less than 2MB');
      return;
    }

    setUploading(true);
    setUploadSuccess(false);
    
    try {
      // Convert file to base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const res = await fetch('/api/b2b/agency/upload-logo', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          base64,
          filename: file.name,
          mimetype: file.type
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Upload failed');
      }

      // Update ONLY the logoUrl in local state (don't reset other fields)
      setSettings(prev => ({ ...prev, logoUrl: data.logoUrl }));
      setUploadSuccess(true);
      
      // Clear success message after 3 seconds
      setTimeout(() => setUploadSuccess(false), 3000);
      
    } catch (err) {
      console.error('Logo upload failed:', err);
      alert('Failed to upload logo: ' + err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Remove logo
  const handleRemoveLogo = async () => {
    if (!confirm('Remove your logo?')) return;
    
    setSaving(true);
    try {
      const res = await fetch('/api/b2b/agency/settings', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ logo_url: null })
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to remove logo');
      }
      
      // Update only logoUrl
      setSettings(prev => ({ ...prev, logoUrl: '' }));
    } catch (err) {
      console.error('Failed to remove logo:', err);
      alert('Failed to remove logo: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    
    try {
      const updateData = {
        name: settings.name,
        brand_color: settings.brandColor,
        logo_emoji: settings.logoEmoji,
        widget_greeting: settings.greeting,
        currency: settings.currency
      };
      
      console.log('Saving settings:', updateData);
      
      const res = await fetch('/api/b2b/agency/settings', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.error || 'Failed to save');
      }
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      
    } catch (err) {
      console.error('Failed to save settings:', err);
      alert('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const copyEmbedCode = () => {
    const code = `<script>
  window.airgatiConfig = {
    agencyId: "${agency?.id || 'YOUR_AGENCY_ID'}"
  };
</script>
<script src="https://app.aiagentify.net/widget/chat.js"></script>`;
    
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      {/* Page Header */}
      <div className="b2b-page-header">
        <div>
          <h1 className="b2b-page-title">{currentTab.title}</h1>
          <p className="b2b-page-subtitle">{currentTab.subtitle}</p>
        </div>
      </div>

      {/* Content */}
      <div className="b2b-settings-content">
        {/* Branding Tab */}
        {activeTab === 'branding' && (
          <>
            <div className="b2b-settings-section">
              <h2 className="b2b-settings-section-title">Branding</h2>
              <p className="b2b-settings-section-desc">
                Customize how your widget looks on your website
              </p>

              {/* Agency Name */}
              <div className="b2b-settings-row">
                <div>
                  <div className="b2b-settings-label">Agency Name</div>
                  <div className="b2b-settings-label-desc">Shown in widget header</div>
                </div>
                <input
                  type="text"
                  className="b2b-form-input"
                  value={settings.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  style={{ maxWidth: '300px' }}
                />
              </div>

              {/* Logo Upload Section */}
              <div className="b2b-settings-row">
                <div>
                  <div className="b2b-settings-label">Agency Logo</div>
                  <div className="b2b-settings-label-desc">Displayed in chat header (max 2MB)</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  {/* Logo Preview Box */}
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '12px',
                    border: settings.logoUrl ? '2px solid #10b981' : '2px dashed #e5e7eb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    background: '#f9fafb',
                    position: 'relative'
                  }}>
                    {settings.logoUrl ? (
                      <img 
                        src={settings.logoUrl} 
                        alt="Agency logo" 
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'contain' 
                        }} 
                      />
                    ) : (
                      <span style={{ fontSize: '32px' }}>{settings.logoEmoji}</span>
                    )}
                  </div>
                  
                  {/* Upload Controls */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                      onChange={handleLogoUpload}
                      style={{ display: 'none' }}
                    />
                    <button
                      className="b2b-btn b2b-btn-secondary"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      style={{ fontSize: '13px' }}
                    >
                      {uploading ? '⏳ Uploading...' : '📁 Upload Logo'}
                    </button>
                    {settings.logoUrl && (
                      <button
                        className="b2b-btn"
                        onClick={handleRemoveLogo}
                        disabled={saving}
                        style={{ 
                          fontSize: '13px',
                          background: '#fee2e2',
                          color: '#dc2626',
                          border: '1px solid #fecaca'
                        }}
                      >
                        🗑️ Remove
                      </button>
                    )}
                    {uploadSuccess && (
                      <span style={{ color: '#10b981', fontSize: '12px' }}>
                        ✓ Logo uploaded!
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Fallback Emoji */}
              {!settings.logoUrl && (
                <div className="b2b-settings-row">
                  <div>
                    <div className="b2b-settings-label">Fallback Emoji</div>
                    <div className="b2b-settings-label-desc">Used if no logo uploaded</div>
                  </div>
                  <input
                    type="text"
                    className="b2b-form-input"
                    value={settings.logoEmoji}
                    onChange={(e) => handleChange('logoEmoji', e.target.value)}
                    style={{ width: '80px', textAlign: 'center', fontSize: '24px' }}
                  />
                </div>
              )}

              {/* Brand Color */}
              <div className="b2b-settings-row">
                <div>
                  <div className="b2b-settings-label">Brand Color</div>
                  <div className="b2b-settings-label-desc">Primary color for buttons</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <input
                    type="color"
                    value={settings.brandColor}
                    onChange={(e) => handleChange('brandColor', e.target.value)}
                    style={{ 
                      width: '50px', 
                      height: '40px', 
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  />
                  <input
                    type="text"
                    className="b2b-form-input"
                    value={settings.brandColor}
                    onChange={(e) => handleChange('brandColor', e.target.value)}
                    style={{ width: '100px' }}
                  />
                  {/* Color preview box */}
                  <div style={{
                    width: '100px',
                    height: '40px',
                    borderRadius: '8px',
                    background: `linear-gradient(135deg, ${settings.brandColor}, ${adjustColor(settings.brandColor, -30)})`,
                    border: '1px solid #e5e7eb'
                  }} />
                </div>
              </div>

              {/* Currency */}
              <div className="b2b-settings-row">
                <div>
                  <div className="b2b-settings-label">Currency</div>
                  <div className="b2b-settings-label-desc">Currency for pricing discussions in chat</div>
                </div>
                <select
                  className="b2b-form-input"
                  value={settings.currency}
                  onChange={(e) => handleChange('currency', e.target.value)}
                  style={{ width: '220px' }}
                >
                  <option value="USD">$ USD - US Dollar</option>
                  <option value="INR">₹ INR - Indian Rupee</option>
                  <option value="EUR">€ EUR - Euro</option>
                  <option value="GBP">£ GBP - British Pound</option>
                  <option value="AED">AED - UAE Dirham</option>
                  <option value="CAD">C$ CAD - Canadian Dollar</option>
                  <option value="AUD">A$ AUD - Australian Dollar</option>
                  <option value="SGD">S$ SGD - Singapore Dollar</option>
                  <option value="THB">฿ THB - Thai Baht</option>
                  <option value="MYR">RM MYR - Malaysian Ringgit</option>
                </select>
              </div>

              {/* Greeting Message */}
              <div className="b2b-settings-row">
                <div>
                  <div className="b2b-settings-label">Greeting Message</div>
                  <div className="b2b-settings-label-desc">First message shown to visitors</div>
                </div>
                <textarea
                  className="b2b-form-textarea"
                  value={settings.greeting}
                  onChange={(e) => handleChange('greeting', e.target.value)}
                  style={{ minHeight: '80px' }}
                />
              </div>
            </div>

            {/* Widget Preview Section */}
            <div className="b2b-settings-section">
              <h2 className="b2b-settings-section-title">Widget Preview</h2>
              <p className="b2b-settings-section-desc">See how your widget will look (updates in real-time)</p>

              <div className="b2b-widget-preview" style={{ background: '#f3f4f6', padding: '40px', borderRadius: '16px' }}>
                {/* Chat Window Preview */}
                <div style={{
                  width: '320px',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                  background: 'white'
                }}>
                  {/* Header */}
                  <div style={{ 
                    background: `linear-gradient(135deg, ${settings.brandColor}, ${adjustColor(settings.brandColor, -30)})`,
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    {/* Avatar */}
                    <div style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '10px',
                      background: settings.logoUrl ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      fontSize: '24px',
                      padding: settings.logoUrl ? '4px' : '0'
                    }}>
                      {settings.logoUrl ? (
                        <img 
                          src={settings.logoUrl} 
                          alt="Logo" 
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'contain' 
                          }} 
                        />
                      ) : (
                        settings.logoEmoji
                      )}
                    </div>
                    <div>
                      <div style={{ color: 'white', fontWeight: 600, fontSize: '15px' }}>
                        {settings.name || 'Your Agency'}
                      </div>
                      <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px' }}>
                        ● Online
                      </div>
                    </div>
                  </div>
                  
                  {/* Body */}
                  <div style={{ padding: '20px', minHeight: '120px', background: '#f9fafb' }}>
                    <div style={{
                      background: 'white',
                      padding: '12px 16px',
                      borderRadius: '12px',
                      borderTopLeftRadius: '4px',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                      fontSize: '14px',
                      color: '#374151',
                      maxWidth: '85%'
                    }}>
                      {settings.greeting || 'Hi! How can I help you today?'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', alignItems: 'center' }}>
              <button 
                className="b2b-btn b2b-btn-primary"
                onClick={handleSave}
                disabled={saving}
                style={{
                  background: `linear-gradient(135deg, ${settings.brandColor}, ${adjustColor(settings.brandColor, -30)})`,
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button 
                className="b2b-btn b2b-btn-secondary"
                onClick={() => setInitialized(false)}
              >
                Reset
              </button>
              
              {saveSuccess && (
                <span style={{ 
                  color: '#10b981', 
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  ✓ Settings saved successfully!
                </span>
              )}
            </div>
          </>
        )}

        {/* Embed Code Tab */}
        {activeTab === 'embed' && (
          <div className="b2b-settings-section">
            <h2 className="b2b-settings-section-title">Embed Code</h2>
            <p className="b2b-settings-section-desc">
              Add this code to your website before the &lt;/body&gt; tag
            </p>

            <div className="b2b-embed-code-box">
              <button className="b2b-copy-btn" onClick={copyEmbedCode}>
                {copied ? '✓ Copied!' : '📋 Copy'}
              </button>
              <pre className="b2b-embed-code">
{`<script>
  window.airgatiConfig = {
    agencyId: "${agency?.id || 'YOUR_AGENCY_ID'}"
  };
</script>
<script src="https://app.aiagentify.net/widget/chat.js"></script>`}
              </pre>
            </div>

            <div style={{ marginTop: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px' }}>
                Installation Instructions
              </h3>
              <ol style={{ paddingLeft: '20px', color: '#71717a', lineHeight: 1.8 }}>
                <li>Copy the embed code above</li>
                <li>Open your website's HTML file or template</li>
                <li>Paste the code just before the <code>&lt;/body&gt;</code> tag</li>
                <li>Save and publish your changes</li>
                <li>The chat widget will appear on your website!</li>
              </ol>
            </div>

            <div style={{ 
              marginTop: '24px', 
              padding: '16px', 
              background: '#fef3c7', 
              borderRadius: '12px',
              border: '1px solid #fbbf24'
            }}>
              <div style={{ fontWeight: 600, marginBottom: '4px' }}>💡 Pro Tip</div>
              <div style={{ fontSize: '14px', color: '#92400e' }}>
                Works with WordPress, Squarespace, Wix, Shopify, and any HTML website.
                If you need help with installation, contact our support team.
              </div>
            </div>
          </div>
        )}

        {/* Widget Settings Tab */}
        {activeTab === 'widget' && (
          <div className="b2b-settings-section">
            <h2 className="b2b-settings-section-title">Widget Settings</h2>
            <p className="b2b-settings-section-desc">
              Configure widget behavior and appearance
            </p>

            <div className="b2b-settings-row">
              <div>
                <div className="b2b-settings-label">Widget Position</div>
                <div className="b2b-settings-label-desc">Where the chat bubble appears</div>
              </div>
              <select 
                className="b2b-form-select" 
                style={{ maxWidth: '200px' }}
                value={widgetSettings.position}
                onChange={(e) => {
                  setWidgetSettings(prev => ({ ...prev, position: e.target.value }));
                  setWidgetSaveSuccess(false);
                }}
              >
                <option value="bottom-right">Bottom Right</option>
                <option value="bottom-left">Bottom Left</option>
              </select>
            </div>

            <div className="b2b-settings-row">
              <div>
                <div className="b2b-settings-label">Auto-open Chat</div>
                <div className="b2b-settings-label-desc">Automatically open chat window after visitor lands on your website</div>
              </div>
              <select 
                className="b2b-form-select" 
                style={{ maxWidth: '200px' }}
                value={widgetSettings.autoOpenDelay}
                onChange={(e) => {
                  setWidgetSettings(prev => ({ ...prev, autoOpenDelay: parseInt(e.target.value) }));
                  setWidgetSaveSuccess(false);
                }}
              >
                <option value="0">Never</option>
                <option value="1">Immediately (1 second)</option>
                <option value="3">After 3 seconds</option>
                <option value="5">After 5 seconds</option>
                <option value="10">After 10 seconds</option>
                <option value="30">After 30 seconds</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', alignItems: 'center' }}>
              <button 
                className="b2b-btn b2b-btn-primary"
                disabled={widgetSaving}
                onClick={async () => {
                  setWidgetSaving(true);
                  setWidgetSaveSuccess(false);
                  
                  try {
                    const res = await fetch('/api/b2b/agency/settings', {
                      method: 'PATCH',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                      },
                      body: JSON.stringify({
                        widget_position: widgetSettings.position,
                        auto_open_delay: widgetSettings.autoOpenDelay
                      })
                    });
                    
                    if (!res.ok) {
                      const data = await res.json();
                      throw new Error(data.error || 'Failed to save');
                    }
                    
                    await refreshAgency();
                    setWidgetSaveSuccess(true);
                    setTimeout(() => setWidgetSaveSuccess(false), 3000);
                    
                  } catch (err) {
                    alert('Failed to save: ' + err.message);
                  } finally {
                    setWidgetSaving(false);
                  }
                }}
              >
                {widgetSaving ? 'Saving...' : 'Save Widget Settings'}
              </button>
              
              {widgetSaveSuccess && (
                <span style={{ 
                  color: '#10b981', 
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  ✓ Widget settings saved!
                </span>
              )}
            </div>
          </div>
        )}

        {/* AI Settings Tab */}
        {activeTab === 'ai' && (
          <AISettingsSection 
            agency={agency} 
            token={token} 
            onSaveSuccess={refreshAgency}
          />
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div className="b2b-settings-section">
            <h2 className="b2b-settings-section-title">Notifications</h2>
            <p className="b2b-settings-section-desc">
              Configure how you receive lead notifications
            </p>

            <div className="b2b-settings-row">
              <div>
                <div className="b2b-settings-label">Email Notifications</div>
                <div className="b2b-settings-label-desc">Get notified for new leads</div>
              </div>
              <select 
                className="b2b-form-select" 
                style={{ maxWidth: '200px' }}
                value={notificationSettings.frequency}
                onChange={(e) => {
                  setNotificationSettings(prev => ({ ...prev, frequency: e.target.value }));
                  setNotificationSaveSuccess(false);
                }}
              >
                <option value="instant">Instant</option>
                <option value="hourly">Hourly digest</option>
                <option value="daily">Daily summary</option>
                <option value="never">Never</option>
              </select>
            </div>

            <div className="b2b-settings-row">
              <div>
                <div className="b2b-settings-label">Notification Email</div>
                <div className="b2b-settings-label-desc">Where to send notifications</div>
              </div>
              <input
                type="email"
                className="b2b-form-input"
                placeholder="you@agency.com"
                style={{ maxWidth: '300px' }}
                value={notificationSettings.email}
                onChange={(e) => {
                  setNotificationSettings(prev => ({ ...prev, email: e.target.value }));
                  setNotificationSaveSuccess(false);
                }}
              />
            </div>

            {/* Notification Info */}
            {notificationSettings.frequency === 'instant' && (
              <div style={{ 
                marginTop: '16px', 
                padding: '12px 16px', 
                background: '#ecfdf5', 
                borderRadius: '8px',
                border: '1px solid #10b981',
                fontSize: '14px',
                color: '#065f46'
              }}>
                ✅ <strong>Instant notifications enabled</strong> — You'll receive an email immediately when a new lead is captured.
              </div>
            )}

            {notificationSettings.frequency === 'never' && (
              <div style={{ 
                marginTop: '16px', 
                padding: '12px 16px', 
                background: '#fef3c7', 
                borderRadius: '8px',
                border: '1px solid #f59e0b',
                fontSize: '14px',
                color: '#92400e'
              }}>
                ⚠️ <strong>Notifications disabled</strong> — You won't receive email alerts for new leads. Check your dashboard regularly!
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', alignItems: 'center' }}>
              <button 
                className="b2b-btn b2b-btn-primary"
                disabled={notificationSaving || !notificationSettings.email}
                onClick={async () => {
                  if (!notificationSettings.email) {
                    alert('Please enter a notification email');
                    return;
                  }
                  
                  setNotificationSaving(true);
                  setNotificationSaveSuccess(false);
                  
                  try {
                    const res = await fetch('/api/b2b/agency/settings', {
                      method: 'PATCH',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                      },
                      body: JSON.stringify({
                        notification_frequency: notificationSettings.frequency,
                        notification_email: notificationSettings.email
                      })
                    });
                    
                    if (!res.ok) {
                      const data = await res.json();
                      throw new Error(data.error || 'Failed to save');
                    }
                    
                    setNotificationSaveSuccess(true);
                    setTimeout(() => setNotificationSaveSuccess(false), 3000);
                    
                  } catch (err) {
                    alert('Failed to save: ' + err.message);
                  } finally {
                    setNotificationSaving(false);
                  }
                }}
              >
                {notificationSaving ? 'Saving...' : 'Save Notification Settings'}
              </button>
              
              {notificationSaveSuccess && (
                <span style={{ 
                  color: '#10b981', 
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  ✓ Notification settings saved!
                </span>
              )}
            </div>

            {/* Test Notification */}
            <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '8px' }}>
                🧪 Test Your Notifications
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
                Send a test notification to make sure everything is set up correctly.
              </p>
              <button 
                className="b2b-btn b2b-btn-secondary"
                disabled={!notificationSettings.email}
                onClick={async () => {
                  if (!notificationSettings.email) {
                    alert('Please enter a notification email first');
                    return;
                  }
                  
                  try {
                    const res = await fetch('/api/b2b/notifications/send-lead-email', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        agencyName: agency?.name || 'Your Agency',
                        agencyEmail: notificationSettings.email,
                        leadName: 'Test Lead',
                        leadEmail: 'test@example.com',
                        leadPhone: '+1 (555) 123-4567',
                        tripSummary: {
                          destination: 'Cancun, Mexico',
                          travel_dates: 'March 15-22, 2025',
                          travelers: '2 adults',
                          budget: '$3,000 - $5,000',
                          trip_type: 'Beach vacation',
                          special_requests: 'Ocean view room preferred'
                        }
                      })
                    });
                    
                    if (res.ok) {
                      alert('✅ Test email sent! Check your inbox at ' + notificationSettings.email);
                    } else {
                      const data = await res.json();
                      alert('❌ Failed to send test email: ' + (data.error || 'Unknown error'));
                    }
                  } catch (err) {
                    alert('❌ Error: ' + err.message);
                  }
                }}
              >
                📧 Send Test Email
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Helper to darken/lighten hex color
function adjustColor(hex, amount) {
  if (!hex || hex.length < 4) return '#d4a853';
  
  let color = hex.replace('#', '');
  if (color.length === 3) {
    color = color.split('').map(c => c + c).join('');
  }
  
  const num = parseInt(color, 16);
  let r = (num >> 16) + amount;
  let g = ((num >> 8) & 0x00FF) + amount;
  let b = (num & 0x0000FF) + amount;
  
  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));
  
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}
